import { useState, useEffect, useCallback } from 'react';
import { carrierApi, vinApi } from '../services/api';
import { logger } from '../utils/logger';
import type {
  CarrierProfile,
  Inspection,
  Crash,
  Violation,
  Vehicle,
  CensusData,
} from '../types/api';
import {
  isCarrierProfile,
  isCensusData,
  isInspectionsResponse,
  isViolationsResponse,
} from '../types/api';
import type { BASICScore } from '../types';

/**
 * Shell game alert interface
 */
export interface ShellGameAlert {
  vin: string;
  carriers: Array<{
    dotNumber: number;
    legalName: string;
    firstSeen: string;
    lastSeen: string;
    inspectionCount: number;
    violationCount?: number;
    oosCount?: number;
  }>;
  totalCarriers: number;
  shellGameDetected: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isRentalUnit: boolean;
}

/**
 * Observed units data interface
 */
export interface ObservedUnitsData {
  totalUnits: number;
  validVins: number;
  invalidVins: number;
  uniqueCarriers: number;
  distribution: {
    byMake: Array<{ make: string; count: number }>;
    byYear: Array<{ year: number; count: number }>;
    byType: Array<{ type: string; count: number }>;
  };
  vehicles: Vehicle[];
}

/**
 * Contact match data interface
 */
export interface ContactMatchData {
  dotNumber: number;
  summary: {
    totalMatches: number;
    priorityMatches: number;
    vinMatches: number;
    phoneMatches: number;
    emailMatches: number;
    officerMatches: number;
    addressMatches: number;
  };
  matches: {
    priority: Array<{
      type: string;
      match: string;
      carriers: Array<{
        dotNumber: number;
        legalName: string;
        matchType: string;
        matchValue: string;
      }>;
    }>;
    vins: Array<{
      vin: string;
      carriers: Array<{
        dotNumber: number;
        legalName: string;
      }>;
    }>;
    phone: Array<{
      phone: string;
      carriers: Array<{
        dotNumber: number;
        legalName: string;
      }>;
    }>;
    email: Array<{
      email: string;
      carriers: Array<{
        dotNumber: number;
        legalName: string;
      }>;
    }>;
    officers: Array<{
      name: string;
      carriers: Array<{
        dotNumber: number;
        legalName: string;
      }>;
    }>;
    address: Array<{
      address: string;
      carriers: Array<{
        dotNumber: number;
        legalName: string;
      }>;
    }>;
  };
}

/**
 * Change history data interface
 */
export interface ChangeHistoryData {
  dotNumber: number;
  changes: Array<{
    date: string;
    field: string;
    oldValue: string | null;
    newValue: string | null;
    changeType: string;
  }>;
  changesByCategory: Record<string, unknown>;
  riskAssessment: {
    hasFrequentChanges: boolean;
    hasLocationChanges: boolean;
    hasOfficerChanges: boolean;
    hasBulkChanges: boolean;
    riskLevel: string;
  };
  summary: {
    totalChanges: number;
    suspiciousChanges: number;
    significantChanges: number;
    firstChangeDate: string | null;
    lastChangeDate: string | null;
  };
}

/**
 * Historic stats data interface
 */
export interface HistoricStatsData {
  dotNumber: number;
  carrierName?: string;
  dbaName?: string;
  addDate?: string;
  totalSnapshots: number;
  currentCensus: {
    snapshot_date: string;
    update_type?: string;
    legal_name?: string;
    dba_name?: string;
    power_units: number;
    trucks?: number;
    tractors?: number;
    trailers?: number;
    drivers: number;
    mcs_150_mileage?: string;
    mileage_year?: number;
    entity_type?: string;
    operation_classification?: string;
    carrier_operation?: string;
    cargo_carried?: string;
    hazmat?: boolean;
    address?: string;
  };
  oldestSnapshot?: {
    snapshot_date: string;
    power_units: number;
    drivers: number;
  };
  historicalSnapshots: Array<{
    snapshot_date: string;
    update_type?: string;
    power_units: number;
    drivers: number;
    mcs_150_mileage?: string;
    mileage_year?: number;
    safety_rating?: string;
    out_of_service?: boolean;
    oos_date?: string;
  }>;
}

// API response wrapper types
type ApiResponseData<T> = T[] | { data: T[]; total?: number; hasMore?: boolean };
type ApiResponseDataWithTotal<T> = T[] | { data: T[]; total: number | null };

/**
 * Hook return interface
 */
export interface UseCarrierDataReturn {
  // Data
  profile: CarrierProfile | null;
  inspections: Inspection[];
  crashes: Crash[];
  violations: Violation[];
  vehicles: Vehicle[];
  censusData: CensusData | null;
  observedUnits: ObservedUnitsData | null;
  contactMatches: ContactMatchData | null;
  changeHistory: ChangeHistoryData | null;
  historicStats: HistoricStatsData | null;
  basics: BASICScore[];
  shellGameAlerts: ShellGameAlert[];

  // State
  loading: boolean;
  error: string | null;
  basicsLoading: boolean;
  loadingMore: { [key: string]: boolean };

  // Actions
  loadCarrier: (dotNumber: number) => Promise<void>;
  loadMoreInspections: () => Promise<void>;
  loadMoreViolations: () => Promise<void>;
  loadMoreCrashes: () => Promise<void>;
  refreshData: () => Promise<void>;
  checkShellGame: () => Promise<void>;
  loadChangeHistory: () => Promise<void>;

  // Counts
  totalInspections: number | null;
  totalViolations: number | null;
  totalCrashes: number | null;

  // Additional state
  dataLoaded: { [key: string]: number };
  checkingShellGame: boolean;
  hasCheckedShellGame: boolean;
}

/**
 * Transform functions to convert API responses to expected formats
 */
const transformCrashes = (crashesData: ApiResponseData<Crash>): Crash[] => {
  logger.debug('Transforming crashes data:', crashesData);
  if (!crashesData) return [];
  const dataArray = Array.isArray(crashesData) ? crashesData : (crashesData.data || []);
  logger.debug('Transformed crashes (first item):', dataArray[0]);
  return dataArray as Crash[];
};

const transformInspections = (inspectionsData: ApiResponseDataWithTotal<Inspection>): { data: Inspection[], total: number | null } => {
  logger.debug('🔄 Transforming inspections data:', inspectionsData);
  if (!inspectionsData) return { data: [], total: null };
  const dataArray = Array.isArray(inspectionsData) ? inspectionsData : (inspectionsData.data || []);
  const total = !Array.isArray(inspectionsData) && inspectionsData.total != null
    ? inspectionsData.total
    : null;
  logger.debug('✅ Transformed inspections (first item):', dataArray[0], 'Total:', total);
  return { data: dataArray as Inspection[], total };
};

const transformViolations = (violationsData: ApiResponseData<Violation>): Violation[] => {
  logger.debug('🔄 Transforming violations data:', violationsData);
  if (!violationsData) return [];
  const dataArray = Array.isArray(violationsData) ? violationsData : (violationsData.data || []);
  logger.debug('✅ Transformed violations (first item):', dataArray[0]);
  return dataArray as Violation[];
};

const transformBasics = (basicsData: ApiResponseData<BASICScore>): BASICScore[] => {
  logger.debug("Transforming BASIC scores data:", basicsData);
  if (!basicsData) return [];

  const dataArray = Array.isArray(basicsData)
    ? basicsData
    : (Array.isArray(basicsData.data) ? basicsData.data : []);

  if (dataArray.length === 0) return [];

  // Flatten nested structure: { basic: {...}, source } -> { ...basic.*, source }
  const flattened = dataArray.map((item: any) => {
    if (item.basic) {
      // API returns nested structure
      const basic = item.basic;
      return {
        basicsId: basic.basicsType?.basicsId || basic.basicsId,
        basicsPercentile: basic.basicsPercentile,
        runDate: basic.basicsRunDate || basic.runDate,
        measureValue: basic.measureValue,
        exceededThreshold: basic.exceededFMCSAInterventionThreshold,
        totalInspectionsWithViolation: basic.totalInspectionWithViolation,
        totalViolations: basic.totalViolation,
        basicsType: basic.basicsType,
        estimated: item.estimated,
        source: item.source,
        ...basic
      };
    }
    // Already flat structure
    return item;
  });

  // FIX #7: Deduplicate by basicsId (in case backend returns duplicates)
  const uniqueBasics = Array.from(
    new Map(
      flattened.map((basic: any) => [
        basic.basicsType?.basicsId || basic.basicsId,
        basic
      ])
    ).values()
  );

  logger.debug("Transformed BASIC scores", { 
    original: flattened.length, 
    deduped: uniqueBasics.length,
    first: uniqueBasics[0] 
  });
  
  return uniqueBasics as BASICScore[];
};

/**
 * Custom hook for managing carrier data loading and state
 *
 * This hook encapsulates all data loading logic for the carrier dashboard,
 * including parallel data fetching, progressive loading, and shell game detection.
 *
 * @example
 * ```tsx
 * const {
 *   profile,
 *   inspections,
 *   crashes,
 *   violations,
 *   loading,
 *   error,
 *   loadCarrier,
 *   loadMoreInspections,
 *   refreshData
 * } = useCarrierData();
 *
 * useEffect(() => {
 *   loadCarrier(123456);
 * }, []);
 * ```
 */
export const useCarrierData = (): UseCarrierDataReturn => {
  // Main data states
  const [profile, setProfile] = useState<CarrierProfile | null>(null);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [crashes, setCrashes] = useState<Crash[]>([]);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [basics, setBasics] = useState<BASICScore[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [censusData, setCensusData] = useState<CensusData | null>(null);
  const [observedUnits, setObservedUnits] = useState<ObservedUnitsData | null>(null);
  const [contactMatches, setContactMatches] = useState<ContactMatchData | null>(null);
  const [changeHistory, setChangeHistory] = useState<ChangeHistoryData | null>(null);
  const [historicStats, setHistoricStats] = useState<HistoricStatsData | null>(null);
  const [shellGameAlerts, setShellGameAlerts] = useState<ShellGameAlert[]>([]);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [basicsLoading, setBasicsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState<{ [key: string]: boolean }>({});
  const [checkingShellGame, setCheckingShellGame] = useState(false);
  const [hasCheckedShellGame, setHasCheckedShellGame] = useState(false);

  // Progressive loading state
  const [dataLoaded, setDataLoaded] = useState<{ [key: string]: number }>({
    inspections: 50,
    violations: 50,
    crashes: 30,
  });

  // Total counts for pagination
  const [dataTotals, setDataTotals] = useState<{ [key: string]: number | null }>({
    inspections: null,
    violations: null,
    crashes: null,
  });

  // Current DOT number being loaded
  const [currentDotNumber, setCurrentDotNumber] = useState<number | null>(null);

  /**
   * Load all carrier data in parallel
   */
  const loadCarrier = useCallback(async (dotNumber: number) => {
    setError(null);
    setLoading(true);
    setCurrentDotNumber(dotNumber);

    // Reset progressive loading state
    setDataLoaded({
      inspections: 50,
      violations: 50,
      crashes: 30,
    });
    setLoadingMore({});
    setHasCheckedShellGame(false);
    setShellGameAlerts([]);

    try {
      // Load critical data first (exclude vehicles for faster initial load)
      const results = await Promise.allSettled([
        carrierApi.getProfile(dotNumber),
        carrierApi.getInspections(dotNumber, 50),
        carrierApi.getCrashes(dotNumber, 30),
        carrierApi.getViolations(dotNumber, 50),
        carrierApi.getCensusData(dotNumber),
        carrierApi.getObservedUnits(dotNumber),
        carrierApi.getContactMatches(dotNumber),
      ]);

      logger.debug('All data loaded in parallel:', results);

      // Set profile and show UI immediately (Issue #772)
      if (results[0].status === 'fulfilled') {
        const profileData = results[0].value;
        if (isCarrierProfile(profileData)) {
          setProfile(profileData);
        } else {
          logger.error('Invalid carrier profile structure:', profileData);
          setProfile(null);
        }
      } else {
        setProfile(null);
      }

      // Handle inspections with total count (Issue #776)
      if (results[1].status === 'fulfilled') {
        const inspectionsData = results[1].value;
        // Accept direct array or wrapped response
        if (Array.isArray(inspectionsData)) {
          setInspections(inspectionsData);
        } else if (inspectionsData && typeof inspectionsData === 'object' && 'data' in inspectionsData) {
          const inspectionsResult = transformInspections(inspectionsData as any);
          setInspections(inspectionsResult.data);
          if (inspectionsResult.total !== null) {
            setDataTotals(prev => ({ ...prev, inspections: inspectionsResult.total }));
          }
        } else {
          logger.error('Invalid inspections response:', inspectionsData);
          setInspections([]);
        }
      } else {
        setInspections([]);
      }

      // Handle crashes
      if (results[2].status === 'fulfilled') {
        const crashesData = results[2].value as ApiResponseData<Crash>;
        logger.debug('🔍 Crashes API response:', {
          hasCrashesData: !!crashesData,
          isDirectArray: Array.isArray(crashesData),
          isWrappedArray: crashesData && !Array.isArray(crashesData) && Array.isArray(crashesData.data),
          dataLength: Array.isArray(crashesData) ? crashesData.length : crashesData?.data?.length,
          firstCrash: Array.isArray(crashesData) ? crashesData[0] : crashesData?.data?.[0]
        });
        // Accept both direct array (from api.ts) and wrapped response (for compatibility)
        if (Array.isArray(crashesData) || (crashesData && Array.isArray(crashesData.data))) {
          const transformed = transformCrashes(crashesData);
          logger.debug('✅ Setting crashes:', transformed.length);
          setCrashes(transformed);
        } else {
          logger.error('❌ Invalid crashes response structure:', crashesData);
          setCrashes([]);
        }
      } else {
        logger.error('❌ Crashes API call failed:', results[2].reason);
        setCrashes([]);
      }

      // Handle violations (Issue #777)
      if (results[3].status === 'fulfilled') {
        const violationsData = results[3].value;
        // Accept direct array or wrapped response
        if (Array.isArray(violationsData)) {
          setViolations(violationsData);
        } else if (violationsData && typeof violationsData === 'object' && 'data' in violationsData) {
          setViolations(transformViolations(violationsData as any));
        } else {
          logger.error('Invalid violations response:', violationsData);
          setViolations([]);
        }
      } else {
        setViolations([]);
      }

      // Handle census data (Issue #773)
      if (results[4].status === 'fulfilled') {
        const censusValue = results[4].value;
        // Accept any object with dotNumber
        if (censusValue && typeof censusValue === 'object' && 'dotNumber' in censusValue) {
          setCensusData(censusValue as any);
        } else {
          logger.error('Invalid census data structure:', censusValue);
          setCensusData(null);
        }
      } else {
        setCensusData(null);
      }

      // Handle observed units (Issue #774)
      if (results[5].status === 'fulfilled') {
        const unitsValue = results[5].value;
        if (unitsValue && typeof unitsValue === 'object' && ('summary' in unitsValue || 'vehicles' in unitsValue)) {
          setObservedUnits(unitsValue as unknown as ObservedUnitsData);
        } else {
          logger.error('Invalid observed units structure:', unitsValue);
          setObservedUnits(null);
        }
      } else {
        setObservedUnits(null);
      }

      // Handle contact matches (Issue #775)
      if (results[6].status === 'fulfilled') {
        const matchesValue = results[6].value;
        if (matchesValue && typeof matchesValue === 'object') {
          setContactMatches(matchesValue as unknown as ContactMatchData);
        } else {
          logger.error('Invalid contact matches structure:', matchesValue);
          setContactMatches(null);
        }
      } else {
        setContactMatches(null);
      }

      setLoading(false);

      // Defer BASIC scores loading (non-blocking)
      setTimeout(async () => {
        try {
          setBasicsLoading(true);
          const basicsData = await carrierApi.getBASICScores(dotNumber);
          setBasics(transformBasics(basicsData));
        } catch (error) {
          logger.error('Failed to load BASIC scores:', error);
        } finally {
          setBasicsLoading(false);
        }
      }, 500);

      // Load vehicles separately after main data is displayed
      setTimeout(async () => {
        try {
          const vehicleData = await carrierApi.getVehicles(dotNumber);
          setVehicles(vehicleData || []);
          logger.debug('Vehicles loaded:', vehicleData?.length || 0);
        } catch (err) {
          logger.error('Failed to load vehicles:', err);
          setVehicles([]);
        }
      }, 200);

      // Load historic stats (MCS-150 history) separately
      setTimeout(async () => {
        try {
          const historicData = await carrierApi.getHistoricStats(dotNumber) as Record<string, unknown>;
          // Transform API response to match expected interface
          const snapshots = (historicData.historicalSnapshots as Array<Record<string, unknown>>) || [];
          const transformedData: HistoricStatsData = {
            dotNumber: historicData.dotNumber as number,
            carrierName: historicData.carrierName as string,
            dbaName: historicData.dbaName as string,
            addDate: historicData.addDate as string,
            totalSnapshots: snapshots.length,
            currentCensus: historicData.currentCensus as HistoricStatsData['currentCensus'],
            oldestSnapshot: snapshots.length > 0
              ? snapshots[snapshots.length - 1] as HistoricStatsData['oldestSnapshot']
              : undefined,
            historicalSnapshots: snapshots as HistoricStatsData['historicalSnapshots'],
          };
          setHistoricStats(transformedData);
          logger.debug('Historic stats loaded:', { totalSnapshots: transformedData.totalSnapshots });
        } catch (err) {
          logger.error('Failed to load historic stats:', err);
          setHistoricStats(null);
        }
      }, 300);

      logger.debug('Carrier data loaded successfully with parallel requests');

    } catch (error: unknown) {
      logger.error('Error fetching carrier data:', error);
      const errorMessage = error instanceof Error && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : error instanceof Error ? error.message : 'Carrier not found. Please check the DOT number.';
      setError(errorMessage || 'Carrier not found. Please check the DOT number.');
      setLoading(false);
    }
  }, []);

  /**
   * Load more inspections
   */
  const loadMoreInspections = useCallback(async () => {
    if (!currentDotNumber) return;

    const currentLimit = dataLoaded.inspections;
    const newLimit = currentLimit + 100;

    setLoadingMore({ ...loadingMore, inspections: true });

    try {
      const response = await carrierApi.getInspections(currentDotNumber, newLimit);
      // Use type guard to validate response structure
      if (Array.isArray(response) || isInspectionsResponse(response)) {
        const inspectionsResult = transformInspections(response);
        setInspections(inspectionsResult.data);

        if (inspectionsResult.total !== null) {
          setDataTotals(prev => ({ ...prev, inspections: inspectionsResult.total }));
        }

        setDataLoaded({ ...dataLoaded, inspections: newLimit });
        logger.debug(`Loaded inspections up to limit ${newLimit}`);
      } else {
        logger.error('Invalid inspections response structure:', response);
        setError('Failed to load inspections: Invalid response format');
      }
    } catch (error) {
      logger.error('Error loading more inspections:', error);
      setError('Failed to load more inspections');
    } finally {
      setLoadingMore({ ...loadingMore, inspections: false });
    }
  }, [currentDotNumber, dataLoaded, loadingMore]);

  /**
   * Load more violations
   */
  const loadMoreViolations = useCallback(async () => {
    if (!currentDotNumber) return;

    const currentLimit = dataLoaded.violations;
    const newLimit = currentLimit + 100;

    setLoadingMore({ ...loadingMore, violations: true });

    try {
      const response = await carrierApi.getViolations(currentDotNumber, newLimit);
      // Use type guard to validate response structure
      if (Array.isArray(response) || isViolationsResponse(response)) {
        const violationsResult = transformViolations(response);
        setViolations(violationsResult);

        setDataLoaded({ ...dataLoaded, violations: newLimit });
        logger.debug(`Loaded violations up to limit ${newLimit}`);
      } else {
        logger.error('Invalid violations response structure:', response);
        setError('Failed to load violations: Invalid response format');
      }
    } catch (error) {
      logger.error('Error loading more violations:', error);
      setError('Failed to load more violations');
    } finally {
      setLoadingMore({ ...loadingMore, violations: false });
    }
  }, [currentDotNumber, dataLoaded, loadingMore]);

  /**
   * Load more crashes
   */
  const loadMoreCrashes = useCallback(async () => {
    if (!currentDotNumber) return;

    const currentLimit = dataLoaded.crashes;
    const newLimit = currentLimit + 100;

    setLoadingMore({ ...loadingMore, crashes: true });

    try {
      const response = await carrierApi.getCrashes(currentDotNumber, newLimit);
      const crashesResult = transformCrashes(response as ApiResponseData<Crash>);
      setCrashes(crashesResult);

      setDataLoaded({ ...dataLoaded, crashes: newLimit });
      logger.debug(`Loaded crashes up to limit ${newLimit}`);
    } catch (error) {
      logger.error('Error loading more crashes:', error);
      setError('Failed to load more crashes');
    } finally {
      setLoadingMore({ ...loadingMore, crashes: false });
    }
  }, [currentDotNumber, dataLoaded, loadingMore]);

  /**
   * Refresh all data for the current carrier
   */
  const refreshData = useCallback(async () => {
    if (currentDotNumber) {
      await loadCarrier(currentDotNumber);
    }
  }, [currentDotNumber, loadCarrier]);

  /**
   * Check for shell game activity across all vehicles
   */
  const checkShellGame = useCallback(async () => {
    if (vehicles.length === 0 || checkingShellGame) {
      return;
    }

    setCheckingShellGame(true);
    setShellGameAlerts([]);

    try {
      const validVins = vehicles
        .filter(vehicle => {
          return vehicle.vinStatus === 'VALID' &&
                 vehicle.vin &&
                 vehicle.vin.length === 17 &&
                 !/^0+$/.test(vehicle.vin);
        })
        .map(vehicle => vehicle.vin);

      if (validVins.length === 0) {
        logger.debug('No valid VINs to check for shell game');
        setCheckingShellGame(false);
        return;
      }

      logger.debug(`Checking ${validVins.length} VINs for shell game detection`);

      // Split VINs into batches of 100 (backend limit)
      const batchSize = 100;
      const allAlerts: ShellGameAlert[] = [];

      for (let i = 0; i < validVins.length; i += batchSize) {
        const batch = validVins.slice(i, i + batchSize);
        const batchNumber = Math.floor(i / batchSize) + 1;
        const totalBatches = Math.ceil(validVins.length / batchSize);

        try {
          logger.debug(`Processing batch ${batchNumber} of ${totalBatches}`);
          const results = await vinApi.checkShellGameBatch(batch);

          const batchAlerts: ShellGameAlert[] = results
            .filter((r) => {
              if (r.isRentalUnit) return false;
              if (!r.shellGameDetected) return false;
              return true;
            })
            .map((r) => ({
              vin: r.vin,
              totalCarriers: r.totalCarriers,
              shellGameDetected: r.shellGameDetected,
              isRentalUnit: r.isRentalUnit,
              carriers: r.carriers,
              riskLevel: r.riskLevel || (r.totalCarriers >= 3 ? 'HIGH' : 'MEDIUM')
            }));

          allAlerts.push(...batchAlerts);

          // Add 1-second delay between batches to avoid rate limiting (except for last batch)
          if (i + batchSize < validVins.length) {
            logger.debug(`Waiting 1s before next batch to avoid rate limit`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (batchError: unknown) {
          logger.error(`Batch ${batchNumber} failed:`, batchError);
        }
      }

      // Deduplicate alerts by VIN
      const uniqueAlerts = allAlerts.filter((alert, index, self) =>
        index === self.findIndex(a => a.vin === alert.vin)
      );

      setShellGameAlerts(uniqueAlerts);
      setHasCheckedShellGame(true);
      logger.debug(`Shell game check complete: ${uniqueAlerts.length} alerts found`);
    } catch (e: unknown) {
      logger.error('Shell game check failed:', e);
    } finally {
      setCheckingShellGame(false);
    }
  }, [vehicles, checkingShellGame]);

  /**
   * Load change history for current carrier
   */
  const loadChangeHistory = useCallback(async () => {
    if (!currentDotNumber || changeHistory) return;

    try {
      const result = await carrierApi.getChangeHistory(currentDotNumber);
      setChangeHistory(result as unknown as ChangeHistoryData);
      logger.debug('Change history loaded');
    } catch (err) {
      logger.error('Failed to load change history:', err);
    }
  }, [currentDotNumber, changeHistory]);

  /**
   * Auto-check shell game when vehicles load
   */
  useEffect(() => {
    if (vehicles.length > 0 && !checkingShellGame && !hasCheckedShellGame) {
      // Don't set states here - let checkShellGame handle it
      const timer = setTimeout(() => {
        checkShellGame();
      }, 100);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [vehicles.length, checkingShellGame, hasCheckedShellGame]); // Remove checkShellGame from deps to prevent infinite loop

  return {
    // Data
    profile,
    inspections,
    crashes,
    violations,
    vehicles,
    censusData,
    observedUnits,
    contactMatches,
    changeHistory,
    historicStats,
    basics,
    shellGameAlerts,

    // State
    loading,
    error,
    basicsLoading,
    loadingMore,
    checkingShellGame,
    hasCheckedShellGame,

    // Actions
    loadCarrier,
    loadMoreInspections,
    loadMoreViolations,
    loadMoreCrashes,
    refreshData,
    checkShellGame,
    loadChangeHistory,

    // Counts
    totalInspections: dataTotals.inspections,
    totalViolations: dataTotals.violations,
    totalCrashes: dataTotals.crashes,
    dataLoaded,
  };
};

export default useCarrierData;
